/*
 * PANOU UTILAJ (nivel 2, Faza B3) — vedere HUD per mașină.
 * Cifrele sunt DOAR ale utilajului (etichetate cu numele real), profil de
 * metrici per mașină din GET /api/machines/<id>/panel + grafic 24h din
 * /api/telemetry/sensors/history. Toate acțiunile pleacă cu contextul
 * mașinii și revin prin pilula ↩ (lpShowBackPill) — inclusiv din
 * Detalii complete / ⚙ Configurare (cerință Dan, 5 iul 2026).
 */
import { state, api, escHtml, toast } from './core';
import { lpShowBackPill, lpRemoveBackPill, lpNavToSection, lpActiveSection } from './line-panel';
import type { RandMachineAlarms } from './tipuri-baza';

interface MPTile { key: string; label: string; unit: string; value: any; fresh: boolean; severity: string; }
/** /api/machines/<id>/panel → `alarms.mentenanta_ceruta` (routes/machines.py): mașina cere mentenanță pe HMI,
 *  cu starea lucrărilor noastre pe aceeași cadență (`mentenanta_ceruta_la_noi`) */
interface MentenantaCerutaPanou extends Pick<RandMachineAlarms, 'alarm_code' | 'severity' | 'description_ro' | 'created_at' | 'cadenta_h'> {
    e_mentenanta: number; la_noi?: { stare: string; text: string };
}

let _mpMachineId = '';
let _mpLabel = '';
let _mpLine = '';
let _mpFromSection = 'dashboard';

function _ensureOverlay(): HTMLElement {
    let ov = document.getElementById('machine-panel-overlay');
    if (ov) return ov;
    ov = document.createElement('div');
    ov.id = 'machine-panel-overlay';
    ov.className = 'detail-overlay hidden';
    ov.innerHTML = `<div class="detail-panel mp-panel">
        <div class="mp-header">
            <div class="mp-title-block">
                <div class="mp-breadcrumb" id="mp-breadcrumb"></div>
                <div class="mp-title" id="mp-title"></div>
                <div class="mp-identity" id="mp-identity"></div>
            </div>
            <span class="mp-status" id="mp-status"></span>
            <button type="button" id="mp-close" class="mp-close" title="Închide">✕</button>
        </div>
        <div id="mp-body" class="mp-body"></div>
    </div>`;
    document.body.appendChild(ov);
    ov.querySelector('#mp-close')?.addEventListener('click', () => closeMachinePanel());
    return ov;
}

/* Auto-reîmprospătare (31 aug 2026, raport Dan: „datele rămân înghețate").
 * Panoul se deschidea o singură dată și rămânea așa — spre deosebire de
 * dashboard, care se împrospătează singur. Regula aici:
 *   - la fiecare 15s reîncărcăm DOAR valorile curente (/panel) — alea se schimbă;
 *   - istoricul 24h, scorul de sănătate și trendul pe 7 zile se recitesc o dată
 *     la 5 minute (se mișcă lent, n-are rost să le cerem la fiecare tur);
 *   - reîmprospătarea e „tăcută": fără spinner, cu poziția de derulare și
 *     secțiunile desfăcute păstrate, ca să nu-ți sară pagina sub degete. */
const MP_REFRESH_MS = 15000;
const MP_TICKS_ISTORIC = 20;          // 20 × 15s = 5 minute
let _mpTimer: number | null = null;
let _mpTick = 0;
let _mpCache: { hist: any; health: any; trend: any } = { hist: null, health: null, trend: null };

function _mpStopRefresh(): void {
    if (_mpTimer !== null) { clearInterval(_mpTimer); _mpTimer = null; }
}

export function closeMachinePanel(): void {
    _mpStopRefresh();
    document.getElementById('machine-panel-overlay')?.classList.add('hidden');
}

function _fmtVal(v: any): string {
    const n = parseFloat(v);
    if (isNaN(n)) return escHtml(String(v ?? '—'));
    if (Math.abs(n) >= 10000) return Math.round(n).toLocaleString('ro-RO');
    if (Math.abs(n) >= 100) return n.toFixed(0);
    return n.toFixed(Math.abs(n) < 10 ? 2 : 1);
}

function _timeShort(iso: string | null): string {
    if (!iso) return '';
    try {
        const d = new Date(iso);
        return d.toLocaleString('ro-RO', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
    } catch (_e) { return String(iso); }
}

/** Inel mare de putere (HUD): procent din maximul ultimelor 24h. */
function _powerRing(kw: number | null, max24: number | null): string {
    const size = 148, r = 62, c = 2 * Math.PI * r;
    const pct = (kw != null && max24 && max24 > 0) ? Math.max(0.02, Math.min(1, kw / (max24 * 1.1))) : 0;
    const off = c * (1 - pct);
    return `<div class="mp-ring-wrap">
      <svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}" role="img" aria-label="Putere activă">
        <defs>
          <linearGradient id="mpRingGrad" x1="0" y1="1" x2="1" y2="0">
            <stop offset="0%" stop-color="var(--cyan,#00c8ff)"/>
            <stop offset="100%" stop-color="var(--green,#00e676)"/>
          </linearGradient>
        </defs>
        <circle cx="${size / 2}" cy="${size / 2}" r="${r}" fill="none" stroke="var(--border,#1e2840)" stroke-width="9"/>
        <circle class="mp-ring-arc" cx="${size / 2}" cy="${size / 2}" r="${r}" fill="none"
            stroke="url(#mpRingGrad)" stroke-width="9" stroke-linecap="round"
            stroke-dasharray="${c.toFixed(1)}" stroke-dashoffset="${off.toFixed(1)}"
            transform="rotate(-90 ${size / 2} ${size / 2})"/>
        <text x="50%" y="47%" text-anchor="middle" class="mp-ring-val">${kw != null ? _fmtVal(kw) : '—'}</text>
        <text x="50%" y="61%" text-anchor="middle" class="mp-ring-unit">kW acum</text>
        ${max24 ? `<text x="50%" y="72%" text-anchor="middle" class="mp-ring-sub">max 24h: ${_fmtVal(max24)}</text>` : ''}
      </svg>
    </div>`;
}

/** Grafic 24h (SVG) cu gradient + glow — pauzele de colectare apar ca goluri. */
function _chart24h(series: Array<{ t: string; avg: number | null }>, unit: string): string {
    const pts = series.filter(p => p.avg != null);
    if (pts.length < 2) return '<div class="mp-chart-empty">Fără istoric pe ultimele 24h.</div>';
    const W = 640, H = 150, PL = 44, PR = 10, PT = 12, PB = 20;
    const vals = pts.map(p => p.avg as number);
    let vmin = Math.min(...vals), vmax = Math.max(...vals);
    if (vmax - vmin < 1e-6) vmax = vmin + 1;
    const pad = (vmax - vmin) * 0.12;
    vmin = Math.max(0, vmin - pad); vmax = vmax + pad;
    const X = (i: number) => PL + (W - PL - PR) * i / (series.length - 1);
    const Y = (v: number) => PT + (H - PT - PB) * (1 - (v - vmin) / (vmax - vmin));
    const segsPts: string[][] = [];
    let seg: string[] = [];
    let segIdx: number[][] = []; let idxs: number[] = [];
    series.forEach((p, i) => {
        if (p.avg == null) { if (seg.length > 1) { segsPts.push(seg); segIdx.push(idxs); } seg = []; idxs = []; return; }
        seg.push(`${X(i).toFixed(1)},${Y(p.avg).toFixed(1)}`); idxs.push(i);
    });
    if (seg.length > 1) { segsPts.push(seg); segIdx.push(idxs); }
    const grid = [0, 0.5, 1].map(f => {
        const v = vmin + (vmax - vmin) * f;
        return `<line x1="${PL}" x2="${W - PR}" y1="${Y(v)}" y2="${Y(v)}" class="mp-grid-line"/>
                <text x="${PL - 6}" y="${Y(v) + 3}" text-anchor="end" class="mp-axis-txt">${_fmtVal(v)}</text>`;
    }).join('');
    const areas = segsPts.map((s, k) => {
        const first = segIdx[k][0], last = segIdx[k][segIdx[k].length - 1];
        return `<polygon points="${X(first).toFixed(1)},${Y(vmin)} ${s.join(' ')} ${X(last).toFixed(1)},${Y(vmin)}" fill="url(#mpAreaGrad)"/>`;
    }).join('');
    const lines = segsPts.map(s =>
        `<polyline class="mp-chart-line" points="${s.join(' ')}" fill="none"/>`).join('');
    const lastSeg = segsPts[segsPts.length - 1];
    const lastPt = lastSeg[lastSeg.length - 1].split(',');
    const labels = [0, Math.floor(series.length / 2), series.length - 1].map(i =>
        `<text x="${X(i)}" y="${H - 5}" text-anchor="middle" class="mp-axis-txt">${(_timeShort(series[i].t).split(' ')[1] || '')}</text>`).join('');
    return `<svg viewBox="0 0 ${W} ${H}" width="100%" height="${H}" preserveAspectRatio="none" role="img" aria-label="Evoluție 24 de ore">
        <defs><linearGradient id="mpAreaGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stop-color="var(--cyan,#00c8ff)" stop-opacity=".28"/>
            <stop offset="100%" stop-color="var(--cyan,#00c8ff)" stop-opacity="0"/>
        </linearGradient></defs>
        ${grid}${areas}${lines}
        <circle class="mp-chart-dot" cx="${lastPt[0]}" cy="${lastPt[1]}" r="4"/>
        ${labels}</svg>
    <div class="mp-chart-cap">ultimele 24h · ${escHtml(unit)} · golurile = pauze de colectare</div>`;
}

function _sevClass(s: string): string { return s === 'crit' ? 'mp-sev-crit' : s === 'warn' ? 'mp-sev-warn' : ''; }

interface MPTrendDay { date: string; pieces: number; running_hours: number | null; pieces_per_hour: number | null; running_estimated?: boolean; }

/** Etichetă zi scurtă (dd.mm) din 'YYYY-MM-DD'. */
function _trendDayLabel(iso: string): string {
    const p = iso.split('-');
    return p.length === 3 ? `${p[2]}.${p[1]}` : iso;
}

/** Formatare compactă piese (ex. 1.05M, 320k). */
function _fmtCaps(n: number): string {
    if (n >= 1e6) return (n / 1e6).toFixed(2).replace(/\.?0+$/, '') + 'M';
    if (n >= 1e3) return Math.round(n / 1e3) + 'k';
    return String(n);
}

/**
 * Grafic TREND producție/zi (SVG): bare = capace/zi, linie = ore de
 * funcționare/zi (axă secundară). Combinația arată throughput real: o zi
 * scurtă cu producție mare are bară înaltă dar linie joasă. Tooltip pe bară
 * dă capace/oră-funcționare (rata normalizată).
 */
function _chartProductionTrend(trend: MPTrendDay[], hasRunning: boolean, unit: string): string {
    if (!trend.length) return '<div class="mp-chart-empty">Fără istoric de producție.</div>';
    const W = 640, H = 172, PL = 48, PR = hasRunning ? 42 : 12, PT = 16, PB = 34;
    const iw = W - PL - PR, ih = H - PT - PB;
    const capsMax = Math.max(1, ...trend.map(d => d.pieces));
    const capsTop = capsMax * 1.15;
    const hoursTop = 24;
    const n = trend.length;
    const bw = Math.max(6, Math.min(46, (iw / n) * 0.62));
    const cx = (i: number) => PL + iw * (i + 0.5) / n;
    const yCaps = (v: number) => PT + ih * (1 - v / capsTop);
    const yHours = (v: number) => PT + ih * (1 - Math.min(v, hoursTop) / hoursTop);

    // grilă orizontală + axă capace (stânga)
    let grid = '';
    [0, 0.5, 1].forEach(f => {
        const v = capsTop * f;
        const y = yCaps(v);
        grid += `<line x1="${PL}" x2="${W - PR}" y1="${y.toFixed(1)}" y2="${y.toFixed(1)}" class="mp-grid-line"/>
            <text x="${PL - 6}" y="${(y + 3).toFixed(1)}" text-anchor="end" class="mp-axis-txt">${_fmtCaps(Math.round(v))}</text>`;
    });
    // axă ore (dreapta) — doar dacă avem timp de funcționare
    if (hasRunning) {
        [0, 12, 24].forEach(hv => {
            const y = yHours(hv);
            grid += `<text x="${W - PR + 6}" y="${(y + 3).toFixed(1)}" text-anchor="start" class="mp-axis-txt mp-axis-hrs">${hv}h</text>`;
        });
    }

    // bare piese/zi
    let bars = '';
    trend.forEach((d, i) => {
        const x = cx(i) - bw / 2;
        const y = yCaps(d.pieces);
        const h = Math.max(0, PT + ih - y);
        const estMark = d.running_estimated ? ' ~' : '';
        const tip = `${_trendDayLabel(d.date)}: ${d.pieces.toLocaleString('ro-RO')} ${unit}`
            + (d.running_hours != null ? ` · ${d.running_hours}h funcționare${estMark}` : '')
            + (d.pieces_per_hour != null ? ` · ${d.pieces_per_hour.toLocaleString('ro-RO')} buc/h` : '');
        bars += `<rect x="${x.toFixed(1)}" y="${y.toFixed(1)}" width="${bw.toFixed(1)}" height="${h.toFixed(1)}"
            rx="3" class="mp-trend-bar"><title>${escHtml(tip)}</title></rect>`;
        // etichetă valoare deasupra barei
        if (d.pieces > 0) {
            bars += `<text x="${cx(i).toFixed(1)}" y="${(y - 4).toFixed(1)}" text-anchor="middle" class="mp-trend-val">${_fmtCaps(d.pieces)}</text>`;
        }
    });

    // linie ore de funcționare + puncte
    let hoursLayer = '';
    if (hasRunning) {
        const hpts = trend.map((d, i) => d.running_hours != null ? `${cx(i).toFixed(1)},${yHours(d.running_hours).toFixed(1)}` : null).filter(Boolean) as string[];
        if (hpts.length >= 2) hoursLayer += `<polyline class="mp-trend-hrs-line" points="${hpts.join(' ')}" fill="none"/>`;
        trend.forEach((d, i) => {
            if (d.running_hours == null) return;
            hoursLayer += `<circle class="mp-trend-hrs-dot" cx="${cx(i).toFixed(1)}" cy="${yHours(d.running_hours).toFixed(1)}" r="3"><title>${escHtml(_trendDayLabel(d.date) + ': ' + d.running_hours + 'h funcționare')}</title></circle>`;
        });
    }

    // etichete zile pe axa X
    const xLabels = trend.map((d, i) =>
        `<text x="${cx(i).toFixed(1)}" y="${H - 16}" text-anchor="middle" class="mp-axis-txt">${_trendDayLabel(d.date)}</text>`).join('');

    const legend = `<div class="mp-trend-legend">
        <span class="mp-trend-lg"><i class="mp-trend-lg-bar"></i>${escHtml(unit)}/zi</span>
        ${hasRunning ? '<span class="mp-trend-lg"><i class="mp-trend-lg-line"></i>ore funcționare/zi</span>' : ''}
    </div>`;

    return `<svg viewBox="0 0 ${W} ${H}" width="100%" height="${H}" role="img" aria-label="Producție pe zi raportată la timpul de funcționare">
        <defs><linearGradient id="mpTrendGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stop-color="var(--cyan,#00c8ff)" stop-opacity=".55"/>
            <stop offset="100%" stop-color="var(--cyan,#00c8ff)" stop-opacity=".12"/>
        </linearGradient></defs>
        ${grid}${bars}${hoursLayer}${xLabels}
    </svg>${legend}
    <div class="mp-chart-cap">bare = capace/zi · linie = ore reale de funcționare (mașina nu rulează non-stop) · treci cu mouse-ul pentru buc/oră</div>`;
}

export async function openMachinePanel(machineId: string, silent = false): Promise<void> {
    const ov = _ensureOverlay();
    const body = ov.querySelector('#mp-body') as HTMLElement;
    // ce trebuie păstrat peste o reîmprospătare tăcută
    const scrollAnt = silent ? body.scrollTop : 0;
    const desfacute = silent
        ? new Set(Array.from(body.querySelectorAll('details.mp-group[open]'))
            .map(d => (d.querySelector('summary')?.textContent || '').trim()))
        : new Set<string>();

    if (!silent) {
        _mpMachineId = machineId;
        _mpFromSection = lpActiveSection();
        lpRemoveBackPill();
        _mpTick = 0;
        _mpCache = { hist: null, health: null, trend: null };
        (ov.querySelector('#mp-title') as HTMLElement).textContent = machineId;
        (ov.querySelector('#mp-breadcrumb') as HTMLElement).textContent = '…';
        (ov.querySelector('#mp-identity') as HTMLElement).textContent = '';
        (ov.querySelector('#mp-status') as HTMLElement).innerHTML = '';
        body.innerHTML = '<div class="mp-loading">Se încarcă panoul utilajului…</div>';
        ov.classList.remove('hidden');
    }

    // istoricul/sănătatea/trendul se mișcă lent — le recitim o dată la 5 minute
    const reciteste = !silent || (_mpTick % MP_TICKS_ISTORIC === 0);
    let data: any = null, hist: any = null, health: any = null, trend: any = null;
    try {
        [data, hist, health, trend] = await Promise.all([
            api('/api/machines/' + encodeURIComponent(machineId) + '/panel'),
            reciteste ? api('/api/telemetry/sensors/history/' + encodeURIComponent(machineId) + '?keys=power&period=24h').catch(() => null) : Promise.resolve(_mpCache.hist),
            reciteste ? api('/api/machines/' + encodeURIComponent(machineId) + '/health').catch(() => null) : Promise.resolve(_mpCache.health),
            reciteste ? api('/api/machines/' + encodeURIComponent(machineId) + '/production_trend?days=7').catch(() => null) : Promise.resolve(_mpCache.trend),
        ]);
        _mpCache = { hist, health, trend };
    } catch (e: any) {
        if (silent) return;   // o eroare pasageră nu strică panoul deja afișat
        body.innerHTML = `<div class="mp-loading">Eroare la încărcare: ${escHtml(e?.message || String(e))}</div>`;
        return;
    }

    const m = data.machine;
    _mpLabel = m.label || machineId;
    _mpLine = m.line || '';
    (ov.querySelector('#mp-breadcrumb') as HTMLElement).textContent =
        (m.line ? `FABRICA ▸ LINIA ${m.line} ▸ UTILAJ` : 'FABRICA ▸ UTILAJ');
    (ov.querySelector('#mp-title') as HTMLElement).textContent = _mpLabel;
    const idBits = [
        m.serial_number ? `S/N ${m.serial_number}` : '',
        m.hours ? `🕐 ${Math.round(m.hours).toLocaleString('ro-RO')} h` : '',
        m.mold ? `🔲 ${m.mold.id} · ${m.mold.cycles.toLocaleString('ro-RO')} cicluri` : '',
        (m.serves || []).length > 1 ? `deservește ${(m.serves || []).join(', ')}` : '',
    ].filter(Boolean).join('  ·  ');
    (ov.querySelector('#mp-identity') as HTMLElement).textContent = idBits;
    const stEl = ov.querySelector('#mp-status') as HTMLElement;
    stEl.innerHTML = data.status === 'running'
        ? '<span class="lp-pill lp-pill-run"><span class="lp-dot"></span>ÎN FUNCȚIUNE</span>'
        : data.status === 'stopped'
            ? '<span class="lp-pill lp-pill-stop">OPRIT</span>'
            : '<span class="lp-pill lp-pill-unk">FĂRĂ DATE</span>';

    const e = data.electric || {};
    const role = state.currentUser?.role || 'operator';
    const isEng = role === 'engineer' || role === 'master';
    const max24 = hist?.kpis?.power?.max ?? null;

    let html = '';

    // ── ETICHETE (18 oct 2026): utilajul de imprimare de pe linia de ambalare — ce va tipări robotul, pe mașini ──
    if (m.type === 'printer') {
        const et = await api('/api/machines/' + encodeURIComponent(machineId) + '/etichete').catch(() => null);
        if (et) {
            const randuri = (et.etichete || []).map((x: any) => {
                const varsta = x.varsta_sec == null ? '' : x.varsta_sec < 90 ? `acum ${x.varsta_sec} s` : `acum ${Math.round(x.varsta_sec / 60)} min`;
                const text = x.text || (x.stare === 'fara_raspuns' ? 'robotul n-a răspuns' : 'necitită încă');
                return `<div class="mp-subscore"><span class="mp-subscore-d">${escHtml(x.linie)}</span>
                    <span class="mp-subscore-t"><b>${escHtml(text)}</b><span class="mp-row-sub">${escHtml(varsta)}</span></span></div>`;
            }).join('');
            html += `<div class="mp-section-title"><span>Ce va tipări robotul</span></div>${randuri}
                <div class="mp-row-sub" style="margin:4px 0 10px">${et.activ ? 'Citit din PLC-ul robotului o dată pe minut.' : 'Citirea etichetelor nu e pornită încă.'}
                Câmpurile și schimbările — în fișa utilajului, fila „Etichete”.</div>`;
        }
    }

    // ── SĂNĂTATE (indice PHM v1) — primul lucru pe care îl vezi ──
    if (health && health.score != null) {
        const sc = health.score;
        const ringColor = sc >= 70 ? 'var(--green,#00e676)' : sc >= 40 ? 'var(--yellow,#ffd600)' : 'var(--red,#ff3d3d)';
        const size = 118, r = 48, c = 2 * Math.PI * r;
        const off = c * (1 - Math.max(0, Math.min(100, sc)) / 100);
        const idleNote = health.status === 'idle'
            ? '<div class="mp-row-sub" style="margin-top:4px">mașină oprită — scorul reflectă alarmele; deviațiile se evaluează în funcțiune</div>' : '';
        let subsHtml = '';
        (health.subscores || []).forEach((s: any) => {
            subsHtml += `<div class="mp-subscore"><span class="mp-subscore-d">${s.delta}</span>
                <span class="mp-subscore-t"><b>${escHtml(s.label)}</b><span class="mp-row-sub">${escHtml(s.detail)}</span></span></div>`;
        });
        if (!subsHtml) subsHtml = '<div class="mp-empty">✓ Toate semnalele în parametri normali</div>';
        html += `<div class="mp-section-title"><span>Sănătate — indice PHM</span></div>
        <div class="mp-hero">
            <div class="mp-ring-wrap" style="filter:drop-shadow(0 0 12px ${ringColor}44)">
              <svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}" role="img" aria-label="Indice de sănătate ${sc} din 100">
                <circle cx="${size / 2}" cy="${size / 2}" r="${r}" fill="none" stroke="var(--border,#1e2840)" stroke-width="8"/>
                <circle class="mp-ring-arc" cx="${size / 2}" cy="${size / 2}" r="${r}" fill="none" stroke="${ringColor}"
                    stroke-width="8" stroke-linecap="round" stroke-dasharray="${c.toFixed(1)}"
                    stroke-dashoffset="${off.toFixed(1)}" transform="rotate(-90 ${size / 2} ${size / 2})"/>
                <text x="50%" y="49%" text-anchor="middle" class="mp-ring-val" style="font-size:26px">${sc}</text>
                <text x="50%" y="64%" text-anchor="middle" class="mp-ring-unit">din 100</text>
              </svg>
            </div>
            <div style="flex:1;min-width:240px">${subsHtml}${idleNote}</div>
        </div>`;
    }

    // ── HERO: inel putere + statistici energie ──
    if (e.power != null || e.kwh_today != null) {
        html += `<div class="mp-hero">
            ${_powerRing(e.power, max24)}
            <div class="mp-hero-tiles">
                ${e.kwh_today != null ? `<div class="mp-tile mp-anim"><div class="mp-tile-v">${_fmtVal(e.kwh_today)}<small> kWh</small></div><div class="mp-tile-l">energie azi${e.kwh_partial ? ' ≈' : ''}</div></div>` : ''}
                ${e.pf_total != null ? `<div class="mp-tile mp-anim"><div class="mp-tile-v">${_fmtVal(e.pf_total)}</div><div class="mp-tile-l">factor putere</div></div>` : ''}
                ${e.imbalance_pct != null ? `<div class="mp-tile mp-anim ${e.imbalance_pct > 10 ? 'mp-sev-warn' : ''}"><div class="mp-tile-v">${_fmtVal(e.imbalance_pct)}<small> %</small></div><div class="mp-tile-l">dezechilibru faze</div></div>` : ''}
                ${hist?.kpis?.power?.avg != null ? `<div class="mp-tile mp-anim"><div class="mp-tile-v">${_fmtVal(hist.kpis.power.avg)}<small> kW</small></div><div class="mp-tile-l">medie 24h</div></div>` : ''}
            </div>
        </div>`;
    }

    // ── OEE azi (dacă mașina are contoarele SACMI) ──
    const oee = (data as any).oee;
    if (oee && oee.oee != null) {
        const bar = (label: string, v: number | null) => {
            const val = v == null ? 0 : v;
            const c = val >= 75 ? 'var(--green,#00e676)' : val >= 50 ? 'var(--yellow,#ffd600)' : 'var(--red,#ff3d3d)';
            return `<div class="mp-oee-row"><span class="mp-oee-lbl">${label}</span>
                <span class="mp-oee-bar"><i style="width:${val}%;background:${c}"></i></span>
                <span class="mp-oee-val">${v == null ? '—' : v.toFixed(0) + '%'}</span></div>`;
        };
        const oc = oee.oee >= 75 ? 'var(--green,#00e676)' : oee.oee >= 50 ? 'var(--yellow,#ffd600)' : 'var(--red,#ff3d3d)';
        html += `<div class="mp-section-title"><span>OEE — eficiență ${oee.partial ? '(azi, parțial)' : 'azi'}</span></div>
        <div class="mp-oee">
            <div class="mp-oee-big" style="color:${oc}">${oee.oee.toFixed(0)}<small>%</small></div>
            <div class="mp-oee-bars">
                ${bar('Disponibilitate', oee.availability)}
                ${bar('Performanță', oee.performance)}
                ${bar('Calitate', oee.quality)}
                ${oee.produced_today != null ? `<div class="mp-row-sub">${oee.produced_today.toLocaleString('ro-RO')} capace în ${oee.window_hours}h</div>` : ''}
            </div>
        </div>`;
    }

    // ── TREND PRODUCȚIE / ZI (raportat la timpul de funcționare) ──
    if (trend && trend.available && Array.isArray(trend.trend) && trend.trend.some((d: MPTrendDay) => d.pieces > 0)) {
        const days: MPTrendDay[] = trend.trend;
        const unit: string = trend.unit || 'piese';
        const totalCaps = days.reduce((s, d) => s + (d.pieces || 0), 0);
        const runDays = days.filter(d => d.running_hours != null);
        const totalHours = runDays.reduce((s, d) => s + (d.running_hours || 0), 0);
        const avgCph = totalHours > 0 ? Math.round(totalCaps / totalHours) : null;
        const titleUnit = unit === 'piese' ? '' : ` — ${unit}`;
        const cavNote = trend.source === 'cycles_x_cavities' && trend.cavities
            ? ` · ${trend.cavities} cavități` : '';
        html += `<div class="mp-section-title"><span>Producție / zi${titleUnit} (raportat la timp funcționare)</span></div>
        <div class="mp-hero-tiles" style="margin-bottom:6px">
            <div class="mp-tile mp-anim"><div class="mp-tile-v">${_fmtCaps(totalCaps)}</div><div class="mp-tile-l">total ${days.length} zile${cavNote}</div></div>
            ${trend.has_running && totalHours > 0 ? `<div class="mp-tile mp-anim"><div class="mp-tile-v">${_fmtVal(totalHours)}<small> h</small></div><div class="mp-tile-l">ore funcționare</div></div>` : ''}
            ${avgCph != null ? `<div class="mp-tile mp-anim"><div class="mp-tile-v">${avgCph.toLocaleString('ro-RO')}</div><div class="mp-tile-l">buc/oră (medie reală)</div></div>` : ''}
        </div>
        <div class="mp-chart">${_chartProductionTrend(days, !!trend.has_running, unit)}</div>`;
    }

    // ── PROCES (profilul mașinii) ──
    if ((data.tiles || []).length) {
        html += '<div class="mp-section-title"><span>Proces</span></div><div class="mp-tiles">';
        (data.tiles as MPTile[]).forEach(t => {
            html += `<div class="mp-tile mp-anim ${_sevClass(t.severity)} ${t.fresh ? '' : 'mp-stale'}"
                title="${t.fresh ? escHtml(t.label) : 'Date vechi — ultima actualizare: ' + _timeShort((t as any).updated_at)}">
                <div class="mp-tile-v">${_fmtVal(t.value)}<small> ${escHtml(t.unit)}</small></div>
                <div class="mp-tile-l">${escHtml(t.label)}${t.fresh ? '' : ' ⏸'}</div>
                <span class="mp-tile-bar"></span>
            </div>`;
        });
        html += '</div>';
    }

    // ── GRUPURI DE PROCES (extruder / hidraulică / ulei / termoregulator) ──
    // pliabile, ca panoul să nu devină „dicționar" (cerință Dan)
    ((data as any).process_groups || []).forEach((g: any) => {
        const tilesHtml = (g.tiles as MPTile[]).map(t =>
            `<div class="mp-tile ${_sevClass(t.severity)}">
                <div class="mp-tile-v">${_fmtVal(t.value)}<small> ${escHtml(t.unit)}</small></div>
                <div class="mp-tile-l">${escHtml(t.label)}</div>
            </div>`).join('');
        html += `<details class="mp-group"><summary class="mp-section-title mp-group-sum">
            <span>${g.icon} ${escHtml(g.label)} <small>(${g.tiles.length})</small></span></summary>
            <div class="mp-tiles">${tilesHtml}</div></details>`;
    });

    // ── EVOLUȚIE 24H ──
    const series = hist?.series?.power;
    if (Array.isArray(series) && series.length) {
        html += `<div class="mp-section-title"><span>Evoluție 24h</span></div><div class="mp-chart">${_chart24h(series, 'kW')}</div>`;
    }

    // ── ALARME + MENTENANȚĂ (clickabile → cu revenire) ──
    html += '<div class="mp-two-col">';
    html += '<div><div class="mp-section-title"><span>Alarme</span></div>';
    if ((data.alarms?.active || []).length) {
        data.alarms.active.forEach((a: any) => {
            html += `<button type="button" class="mp-row mp-row-click" data-mact="alarms" title="Deschide istoricul de alarme al utilajului">
                <span class="sevdot ${a.severity === 'critical' ? 'mp-dot-crit' : 'mp-dot-warn'}"></span>
                <span class="mp-row-main"><b>${escHtml(a.alarm_code || '')}</b> ${escHtml((a.description_ro || '').substring(0, 70))}
                <span class="mp-row-sub">${_timeShort(a.created_at)}</span></span>
                <span class="mp-row-go">›</span></button>`;
        });
    } else {
        html += '<div class="mp-empty">✓ Nicio alarmă activă</div>';
    }
    // 11 oct 2026: mașina cere mentenanță pe HMI — nu e defect; spunem ce e la noi pe aceeași cadență
    (data.alarms?.mentenanta_ceruta || []).forEach((a: MentenantaCerutaPanou) => {
        html += `<button type="button" class="mp-row mp-row-click" data-mact="alarms" title="Mașina cere mentenanță — deschide istoricul de alarme">
            <span class="mp-row-main">🔧 <b>${escHtml(a.alarm_code || '')}</b> ${escHtml((a.description_ro || '').substring(0, 70))}
            <span class="mp-row-sub">${escHtml(a.la_noi?.text || '')}</span></span>
            <span class="mp-row-go">›</span></button>`;
    });
    html += `<div class="mp-row-sub" style="margin-top:6px">${data.alarms?.last24h || 0} alarme în 24h</div></div>`;

    // ── Împărțirea energiei pe linii (utilități partajate) ──────────────────
    // Decizia lui Dan, 20 aug 2026: ponderile de utilizare (când TOATE liniile
    // ar merge) se introduc manual AICI, pe utilitate — un singur loc, suma
    // controlată la 100. Împărțirea reală pe lună o face serverul, renormalizând
    // pe liniile efectiv pornite, oră cu oră.
    const imp = (data as any).impartire_energie;
    if (imp && (imp.linii || []).length > 1) {
        const pond = imp.ponderi || {};
        const aloc = imp.alocare_luna;
        html += '<div><div class="mp-section-title"><span>Împărțire energie pe linii</span></div>';
        html += '<div class="mp-row-sub" style="margin-bottom:6px">Procente când toate liniile merg — împărțirea reală se recalculează pe cine e pornit, oră cu oră.</div>';
        html += '<div class="mp-impartire">';
        (imp.linii as string[]).forEach(lid => {
            html += `<label class="mp-imp-rand">${escHtml(lid)}
                <input type="number" class="mp-imp-procent" min="0" max="100" step="1"
                       data-linie="${escHtml(lid)}" value="${pond[lid] ?? ''}"
                       placeholder="%" aria-label="Procent ${escHtml(lid)}"> %</label>`;
        });
        html += `<button type="button" class="mp-imp-salveaza" data-mid="${escHtml(data.machine.id)}">💾 Salvează</button>`;
        html += '<span class="mp-imp-suma mp-row-sub"></span>';
        html += '</div>';
        if (aloc) {
            const detaliu = Object.entries(aloc.pe_linii as Record<string, number>)
                .sort((a, b) => b[1] - a[1])
                .map(([l, v]) => `${escHtml(l)}: ${Math.round(v).toLocaleString('ro-RO')} kWh`)
                .join(' · ');
            html += `<div class="mp-row-sub" style="margin-top:4px">Luna asta: ${Math.round(aloc.total).toLocaleString('ro-RO')} kWh → ${detaliu}`
                + (aloc.standby > 1 ? ` · standby ${Math.round(aloc.standby).toLocaleString('ro-RO')} kWh` : '') + '</div>';
        } else if (!Object.keys(pond).length) {
            html += '<div class="mp-row-sub" style="margin-top:4px">Fără ponderi setate — consumul se raportează doar nedespărțit.</div>';
        }
        html += '</div>';
    }

    html += '<div><div class="mp-section-title"><span>Mentenanță scadentă</span></div>';
    if ((data.next_due || []).length) {
        data.next_due.forEach((d: any) => {
            // 1 nov 2026 (auditul surselor de ore): starea și textul vin de pe server, din sursa unică — pagina nu mai
            // rescrie „depășită” după ore brute (o lunară apărea „depășită cu 393 h” deși nu era restantă)
            const dot = d.nivel ? (d.nivel === 'depasit' ? 'mp-dot-crit' : d.nivel === 'aproape' ? 'mp-dot-warn' : 'mp-dot-ok')
                : (d.overdue ? 'mp-dot-crit' : 'mp-dot-ok');
            const sub = d.text ? escHtml(d.text)
                : d.overdue ? 'depășită' : (d.remaining_h != null ? `în ${Number(d.remaining_h).toLocaleString('ro-RO')} h` : '');
            html += `<button type="button" class="mp-row mp-row-click" data-mact="tasks" title="Deschide Lucrările mele pentru acest utilaj">
                <span class="sevdot ${dot}"></span>
                <span class="mp-row-main">${escHtml(d.action)}
                <span class="mp-row-sub">${sub}</span></span>
                <span class="mp-row-go">›</span></button>`;
        });
        if (data.overdue_count > 3) html += `<div class="mp-row-sub">… + încă ${data.overdue_count - 3} depășite</div>`;
    } else if (data.plan_mentenanta && data.plan_mentenanta.intervale === 0) {
        /* 29 sep 2026: fără nicio lucrare în plan, „✓ Nimic scadent" spunea că e
           totul în regulă. Nu e — utilajul n-are plan (robot, tablou, TAMPO…). */
        html += data.plan_mentenanta.documente > 0
            ? '<div class="mp-empty mp-fara-plan">⚠ Fără plan de mentenanță — manualul e în aplicație, dar tabelul lui de întreținere nu e încă transcris.</div>'
            : '<div class="mp-empty mp-fara-plan">⚠ Fără plan de mentenanță — lipsește manualul utilajului.</div>';
    } else {
        html += '<div class="mp-empty">✓ Nimic scadent</div>';
    }
    html += '</div></div>';

    // ── contoare de mentenanță ALE MAȘINII (SACMI, sursă directă) ──
    const mm = (data as any).machine_maint || [];
    if (mm.length) {
        const overdueN = mm.filter((x: any) => x.overdue).length;
        html += `<details class="mp-group"><summary class="mp-section-title mp-group-sum">
            <span>🔧 Contoare mentenanță — raportate de mașină ${overdueN ? `<b class="lp-warn">(${overdueN} depășite)</b>` : ''}</span></summary>
            <div class="mp-maint-note">Orele scurse vin direct de la mașină (contoarele SACMI), nu din istoricul nostru — sursă de adevăr pentru starea reală.</div>`;
        mm.forEach((x: any) => {
            const c = x.overdue ? 'var(--red,#ff3d3d)' : x.pct >= 80 ? 'var(--yellow,#ffd600)' : 'var(--green,#00e676)';
            html += `<div class="mp-maint-row">
                <span class="mp-maint-tgt">${x.target_h.toLocaleString('ro-RO')}h</span>
                <span class="mp-oee-bar"><i style="width:${Math.min(100, x.pct)}%;background:${c}"></i></span>
                <span class="mp-maint-el">${x.overdue
                    ? `<span style="color:${c}">depășit +${Math.abs(x.remaining_h).toLocaleString('ro-RO')}h</span>`
                    : `${x.remaining_h.toLocaleString('ro-RO')}h rămase`}</span>
            </div>`;
        });
        html += '</details>';
    }

    // ── ULTIMELE INTERVENȚII ──
    html += '<div class="mp-section-title"><span>Ultimele intervenții</span></div>';
    if ((data.interventions || []).length) {
        data.interventions.forEach((iv: any) => {
            html += `<button type="button" class="mp-row mp-row-click" data-log="${iv.id}">
                <span class="sevdot ${iv.category === 'urgent' ? 'mp-dot-warn' : 'mp-dot-ok'}"></span>
                <span class="mp-row-main"><b>${escHtml(iv.registration_number || ('#' + iv.id))}</b> ${escHtml(iv.action || '')}
                <span class="mp-row-sub">${escHtml(iv.date || '')} · ${escHtml(iv.technician || '')}${iv.duration_minutes ? ' · ' + iv.duration_minutes + ' min' : ''}</span></span>
                <span class="mp-row-go">›</span></button>`;
        });
    } else {
        html += '<div class="mp-empty">Nicio intervenție înregistrată</div>';
    }

    // ── ACȚIUNI ──
    html += `<div class="mp-actions">
        <button type="button" class="mp-btn mp-btn-urgent" data-mact="urgent">🚨 Intervenție urgentă</button>
        <button type="button" class="mp-btn" data-mact="tasks">🔧 Lucrările mele</button>
        <button type="button" class="mp-btn" data-mact="alarms">🔔 Alarme utilaj</button>
        <button type="button" class="mp-btn" data-mact="ai">🤖 Întreabă AI</button>
        <button type="button" class="mp-btn" data-mact="full">🗂 Detalii complete</button>
        ${isEng ? '<button type="button" class="mp-btn mp-btn-config" data-mact="config">⚙ Configurare</button>' : ''}
    </div>`;

    body.innerHTML = html;
    _wireActions(body);

    if (silent) {
        // punem la loc exact ce avea utilizatorul sub ochi
        body.querySelectorAll('details.mp-group').forEach(d => {
            const t = (d.querySelector('summary')?.textContent || '').trim();
            if (desfacute.has(t)) (d as HTMLDetailsElement).open = true;
        });
        body.scrollTop = scrollAnt;
    } else {
        _mpStopRefresh();
        _mpTimer = window.setInterval(() => {
            const ovv = document.getElementById('machine-panel-overlay');
            if (!ovv || ovv.classList.contains('hidden') || !_mpMachineId) {
                _mpStopRefresh();       // panoul s-a închis — nu mai cerem date
                return;
            }
            // dacă utilizatorul chiar lucrează în panou (a dat focus pe un
            // buton/câmp), sărim turul — altfel îi reconstruim DOM-ul sub deget
            const activ = document.activeElement;
            if (activ && ovv.contains(activ) && activ !== document.body) return;
            _mpTick += 1;
            void openMachinePanel(_mpMachineId, true);
        }, MP_REFRESH_MS);
    }
}

/** Pleacă din panou cu contextul mașinii; pilula ↩ redeschide panoul. */
function _goWithReturn(go: () => void): void {
    const mid = _mpMachineId;
    const from = _mpFromSection;
    closeMachinePanel();
    go();
    setTimeout(() => lpShowBackPill(`↩ Înapoi la ${_mpLabel}`, () => {
        lpNavToSection(from);
        openMachinePanel(mid);
    }), 500);
}

/** Detaliul vechi (Detalii complete / Configurare) cu revenire la panou. */
function _openDetailWithReturn(source: string): void {
    const mid = _mpMachineId, label = _mpLabel;
    const w = window as any;
    closeMachinePanel();
    w.openLineDetail?.(mid, label, source);
    setTimeout(() => lpShowBackPill(`↩ Înapoi la ${label}`, () => {
        document.getElementById('detail-overlay')?.classList.add('hidden');
        openMachinePanel(mid);
    }), 400);
}

function _wireActions(body: HTMLElement): void {
    const w = window as any;

    // Editorul de ponderi al utilității partajate (20 aug 2026)
    const btnSalv = body.querySelector('.mp-imp-salveaza') as HTMLElement | null;
    if (btnSalv) {
        const campuri = () => Array.from(body.querySelectorAll<HTMLInputElement>('.mp-imp-procent'));
        const sumaEl = body.querySelector('.mp-imp-suma') as HTMLElement | null;
        const arataSuma = () => {
            const s = campuri().reduce((a, c) => a + (parseFloat(c.value) || 0), 0);
            if (sumaEl) {
                sumaEl.textContent = `Σ ${s.toFixed(0)}%`;
                sumaEl.style.color = Math.abs(s - 100) <= 0.5 ? 'var(--green, #00e676)' : 'var(--red, #ef5350)';
            }
        };
        campuri().forEach(c => c.addEventListener('input', arataSuma));
        arataSuma();
        btnSalv.addEventListener('click', async () => {
            const ponderi: Record<string, number> = {};
            campuri().forEach(c => { ponderi[c.dataset.linie!] = parseFloat(c.value) || 0; });
            const suma = Object.values(ponderi).reduce((a, b) => a + b, 0);
            if (Math.abs(suma - 100) > 0.5) {
                toast(`Suma procentelor trebuie să fie 100 (acum ${suma.toFixed(0)}%)`, 'error');
                return;
            }
            btnSalv.textContent = '⏳...';
            try {
                await api(`/api/utilitati/${encodeURIComponent(btnSalv.dataset.mid || '')}/ponderi`, {
                    method: 'PUT', headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ ponderi }),
                });
                toast('Ponderi salvate — împărțirea lunii s-a recalculat', 'success');
                openMachinePanel(btnSalv.dataset.mid || '');
            } catch (e: any) {
                toast('Nu am putut salva: ' + (e?.message || e), 'error');
                btnSalv.textContent = '💾 Salvează';
            }
        });
    }
    body.querySelectorAll('[data-log]').forEach(row => {
        row.addEventListener('click', () => {
            const id = (row as HTMLElement).dataset.log;
            if (id && typeof w.openLogDetail === 'function') w.openLogDetail(id);
        });
    });
    body.querySelectorAll('[data-mact]').forEach(btn => {
        btn.addEventListener('click', () => {
            const act = (btn as HTMLElement).dataset.mact;
            const mid = _mpMachineId, line = _mpLine;
            const role = state.currentUser?.role || 'operator';
            switch (act) {
                case 'urgent':
                    _goWithReturn(() => {
                        lpNavToSection('journal');
                        setTimeout(() => {
                            const lineSel = document.getElementById('jrn-line') as HTMLSelectElement | null;
                            if (lineSel && line) { lineSel.value = line; lineSel.dispatchEvent(new Event('change')); }
                            setTimeout(() => {
                                const mSel = document.getElementById('jrn-machine') as HTMLSelectElement | null;
                                if (mSel) mSel.value = mid;
                            }, 300);
                        }, 450);
                    });
                    break;
                case 'tasks':
                    _goWithReturn(() => {
                        lpNavToSection('mytasks');
                        setTimeout(() => {
                            const lf = document.getElementById('mytasks-line-filter') as HTMLSelectElement | null;
                            if (lf && line) { lf.value = line; lf.dispatchEvent(new Event('change')); }
                            setTimeout(() => {
                                const mf = document.getElementById('mytasks-machine-filter') as HTMLSelectElement | null;
                                if (mf) { mf.value = mid; mf.dispatchEvent(new Event('change')); }
                            }, 300);
                        }, 400);
                    });
                    break;
                case 'alarms':
                    _goWithReturn(() => w.openAlarmHistoryForLine?.(line, mid));
                    break;
                case 'ai':
                    _goWithReturn(() => w._dashAskAI?.(line, mid));
                    break;
                case 'full':
                    _openDetailWithReturn(role === 'operator' ? 'operator' : 'master');
                    break;
                case 'config':
                    if (role === 'operator') { toast('Doar inginer/master.', 'warning'); return; }
                    _openDetailWithReturn('master');
                    break;
            }
        });
    });
}

// expunere globală (chips-urile din line-panel + alte module apelează prin window)
(window as any).openMachinePanel = openMachinePanel;
(window as any).closeMachinePanel = closeMachinePanel;
